import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { HeaderComponent } from '@core-app/layout/header/header.component';
import { pickContrastingTextColor } from '@gallaghercp/core-frontend';

@Component({
    selector: 'core-brand-logo',
    template: `@if (isNotLink) {
            <div class="brand-logo">
                <ng-container *ngTemplateOutlet="logoWrapper"></ng-container>
            </div>
        } @else {
            <a
                [core-analytics]="{ action: 'Home', category: category || 'TOP-NAV' }"
                class="brand-logo"
                [ngClass]="additionalClasses"
                href="javascript:void(0)"
                aria-label="Navigate home"
                (click)="navigateToHome()"
            >
                <ng-container *ngTemplateOutlet="logoWrapper"></ng-container>
            </a>
        }
        <ng-template #logoWrapper>
            <div class="brand-logo__logo-wrapper">
                <img [alt]="getSiteName()" [ngSrc]="displayedLogoUrl | cdn" class="brand-logo__logo" fill priority />
            </div>
        </ng-template>`,
    standalone: false,
})
export class BrandLogoComponent extends HeaderComponent implements OnInit, OnChanges {
    @Input() category: string | null = null;
    @Input() isNotLink: boolean | null = null;
    @Input() additionalClasses: string | null = null;
    @Input() bgHeader?: string;
    @Input() logoUrlOverride?: string;

    displayedLogoUrl!: string;

    protected logoStandard!: string;
    protected logoStandardTransparent!: string;
    protected logoInverted!: string;

    /**
     * Compute a background-aware logo URL using the same contrast rules as TopNav.
     * Footer and other placements can benefit without duplicating logic.
     */
    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.push(
            this.settings.getLogo().subscribe((logoUrl: any) => {
                this.logoStandard = logoUrl;
                this.computeLogoFromBg();
            }),
            this.settings.getInvertedLogo().subscribe((logoUrl: any) => {
                this.logoInverted = logoUrl;
                this.computeLogoFromBg();
            }),
            this.settings.getTransparentLogo().subscribe((logoUrl: any) => {
                this.logoStandardTransparent = logoUrl;
                this.computeLogoFromBg();
            }),
        );

        this.computeLogoFromBg();
    }

    ngOnChanges(): void {
        this.computeLogoFromBg();
    }

    private computeLogoFromBg(): void {
        try {
            const configColors: any = this.config.get('colors');
            const categoryUpper = (this.category || '').toUpperCase();
            const isHeader = categoryUpper === 'TOP-NAV' || categoryUpper === 'HEADER';
            const isFooter = categoryUpper === 'FOOTER';

            let effectiveBg: string | undefined;
            if (isHeader) {
                // Prefer explicit bgHeader if available and not transparent,
                // then elementBackgroundColour, then configColors.headerBg, then navBg
                const elementBg = (configColors as any)?.elementBackgroundColour;
                const configHeaderBg = (configColors as any)?.headerBg;
                const navBg = (configColors as any)?.navBg ?? (configColors as any)?.bg ?? elementBg ?? configHeaderBg;
                effectiveBg =
                    this.bgHeader && this.bgHeader !== 'transparent'
                        ? this.bgHeader
                        : elementBg || configHeaderBg || navBg;
            } else if (isFooter) {
                // Strictly honor footerBg; otherwise fall back to elementBackgroundColour only
                // If footerBg is missing or empty string, use elementBackgroundColour
                const hasFooterBg =
                    typeof configColors?.footerBg === 'string' && configColors.footerBg.trim().length > 0;
                effectiveBg = hasFooterBg ? configColors.footerBg : configColors?.elementBackgroundColour;
            } else {
                // Default placement: use element background as safe baseline
                effectiveBg = configColors?.elementBackgroundColour;
            }

            // Final fallback to ensure we never pass undefined into contrast calc
            let bgForContrast = effectiveBg || configColors?.elementBackgroundColour;

            if (!bgForContrast || configColors?.elementBackgroundColour === false) {
                bgForContrast = this.darkMode ? '#000000' : '#ffffff';
            }

            const contrast = pickContrastingTextColor(bgForContrast);
            if (this.logoUrlOverride) {
                this.displayedLogoUrl = this.logoUrlOverride;
            } else if (isHeader && this.isTransparentNav && !this.isTopNavTransparentSticky) {
                this.displayedLogoUrl = this.logoStandardTransparent || this.logoStandard;
            } else {
                this.displayedLogoUrl = contrast === 'white' ? this.logoInverted : this.logoStandard;
            }
        } catch {
            this.displayedLogoUrl = this.logoStandard;
        }
    }

    // Ensure that whenever the base class or subscriptions request a logo update,
    // we apply the contrast-based logic for header/footer.
    override updateLogoUrl(): void {
        // Compute based on effective background (header/footer/default)
        this.computeLogoFromBg();
    }
}
